import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Flex, Row } from 'antd';
import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import CustomerExplanationPanel from '@totem/components/ticketing/details/customerExplanation/customerExplanationPanel';
import HeaderEditDialog from '@totem/components/ticketing/details/dialogs/headerEditDialog';
import OptimizePanel from '@totem/components/ticketing/details/optimize/optimizePanel';
import TechnicianActionsPanel from '@totem/components/ticketing/details/technicianActions/technicianActionsPanel';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import StatusDisplayExtended from '@totem/components/ticketing/statusDisplayExtended';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import TagDisplay, {
  TICKET_IMPACT_STYLES,
  TICKET_PRIORITY_STYLES,
  TICKET_URGENCY_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { formatDate, isNotNull } from '@totem/utilities/common';

import '../ticketing.css';

const styles = {
  description: {
    whiteSpace: 'pre-wrap' as 'pre-wrap',
  },
};

type Props = {
  ticket: TicketQueryResult;
};

const TicketHeader = ({ ticket }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { action, onAction } = useContext(TicketContext);

  const getBuildingDisplay = (ticketData: TicketQueryResult) => {
    if (
      typeof ticketData !== 'undefined' &&
      ticketData !== null &&
      ticketData.building !== null &&
      ticketData.building.id !== null &&
      ticketData.building.id !== EMPTY_ID &&
      ticketData.organization.id === userProfile.organization.id
    ) {
      return (
        <Link to={`/dashboard/buildings/${ticketData.building.id}`}>
          {ticketData.building.name}
        </Link>
      );
    } else if (ticketData.building !== null) {
      return <span>{ticketData.building.name}</span>;
    }
    return <span>-</span>;
  };

  const getControlSystemAlternate = (ticketData: TicketQueryResult) => {
    if (
      typeof ticketData !== 'undefined' &&
      ticketData !== null &&
      ticketData.ticket.attributes !== null
    ) {
      for (let idx = 0; idx < ticketData.ticket.attributes.length; idx++) {
        const attribute = ticketData.ticket.attributes[idx];
        if (attribute.name === 'ControlSystemType') {
          return attribute.value;
        }
      }
    }
    return '';
  };

  const getControlSystemDisplay = (ticketData: TicketQueryResult) => {
    if (
      typeof ticketData !== 'undefined' &&
      ticketData !== null &&
      ticketData.controlSystem !== null &&
      ticketData.controlSystem.id !== null &&
      ticketData.controlSystem.id !== EMPTY_ID &&
      ticketData.organization.id === userProfile.organization.id
    ) {
      return (
        <Link to={`/dashboard/controlsystems/${ticketData.controlSystem.id}`}>
          {ticketData.controlSystem.name}
        </Link>
      );
    } else if (
      ticketData.controlSystem !== null &&
      ticketData.controlSystem.name !== ''
    ) {
      return <span>{ticketData.controlSystem.name}</span>;
    }

    return <span>{getControlSystemAlternate(ticketData)}</span>;
  };

  const getDeviceDisplay = (ticketData: TicketQueryResult) => {
    if (
      typeof ticketData !== 'undefined' &&
      ticketData !== null &&
      ticketData.device !== null &&
      ticketData.device.id !== null &&
      ticketData.device.id !== EMPTY_ID &&
      ticketData.organization.id === userProfile.organization.id
    ) {
      return (
        <Link to={`/dashboard/devices/${ticketData.device.id}`}>
          {ticketData.device.name}
        </Link>
      );
    } else if (ticketData.device !== null) {
      return <span>{ticketData.device.name}</span>;
    }
    return <span>-</span>;
  };

  const getContractNumber = (ticketData: TicketQueryResult) => {
    if (isNotNull(ticketData.ticket.keys)) {
      const contractKey = ticketData.ticket.keys.find(
        (chk) => chk.system === 'Contracts' && chk.instance === 'NetSuite',
      );
      if (isNotNull(contractKey)) {
        return contractKey.key;
      }
    }
    return '';
  };

  const salesOrder = useMemo(() => {
    return getContractNumber(ticket);
  }, [ticket]);

  const canSeeContract = useMemo(() => {
    return permissions.internal.hasPermissions(userProfile);
  }, [userProfile]);

  return (
    <div>
      <Row>
        <Col span={6}>
          <HeaderItem title="Summary">{ticket.ticket.summary}</HeaderItem>
          <HeaderItem title="Status">
            <StatusDisplayExtended ticket={ticket.ticket} />
          </HeaderItem>
          {isNotNull(ticket.ticket.impact) && ticket.ticket.impact !== '' && (
            <HeaderItem title="Impact">
              <TagDisplay
                value={ticket.ticket.impact}
                config={TICKET_IMPACT_STYLES}
              />
            </HeaderItem>
          )}
          {isNotNull(ticket.ticket.urgency) && ticket.ticket.urgency !== '' && (
            <HeaderItem title="Urgency">
              <TagDisplay
                value={ticket.ticket.urgency}
                config={TICKET_URGENCY_STYLES}
              />
            </HeaderItem>
          )}
          <HeaderItem title="Priority">
            <TagDisplay
              value={ticket.ticket.priority}
              config={TICKET_PRIORITY_STYLES}
            />
          </HeaderItem>
        </Col>
        <Col span={6}>
          {typeof ticket.ticket.category !== 'undefined' &&
            ticket.ticket.category !== null &&
            ticket.ticket.category !== '' && (
              <HeaderItem title="Category">{ticket.ticket.category}</HeaderItem>
            )}
          {typeof ticket.ticket.subCategory !== 'undefined' &&
            ticket.ticket.subCategory !== null &&
            ticket.ticket.subCategory !== '' && (
              <HeaderItem title="SubCategory">
                {ticket.ticket.subCategory}
              </HeaderItem>
            )}
          {canSeeContract && salesOrder !== '' && (
            <HeaderItem title="Contract">{salesOrder}</HeaderItem>
          )}
        </Col>
        <Col span={6}>
          <HeaderItem title="Organization">
            {ticket.organization.name}
          </HeaderItem>
          {ticket.region.name !== '' && (
            <HeaderItem title="Region">{ticket.region.name}</HeaderItem>
          )}
          {ticket.building.name !== '' && (
            <HeaderItem title="Building">
              {getBuildingDisplay(ticket)}
            </HeaderItem>
          )}
          {(ticket.controlSystem.name !== '' ||
            getControlSystemAlternate(ticket) !== '') && (
            <HeaderItem title="Control System">
              {getControlSystemDisplay(ticket)}
            </HeaderItem>
          )}
          {ticket.device.name !== '' && (
            <HeaderItem title="Device">{getDeviceDisplay(ticket)}</HeaderItem>
          )}
        </Col>
        <Col span={6}>
          <Flex justify="space-between" align="flex-start">
            <div>
              <HeaderItem title="Created By">
                {ticket.ticket.createdByName}
              </HeaderItem>
              <HeaderItem title="Created">
                {formatDate(ticket.ticket.createdAt)}
              </HeaderItem>
              <HeaderItem title="Last Updated">
                {formatDate(ticket.ticket.updatedAt)}
              </HeaderItem>
              {isNotNull(ticket.ticket.followUpAt) &&
                ticket.ticket.followUpAt !== '' && (
                  <HeaderItem title="Follow Up">
                    {dayjs(ticket.ticket.followUpAt).isAfter(dayjs()) ? (
                      <span>{formatDate(ticket.ticket.followUpAt)}</span>
                    ) : (
                      <span style={{ color: 'red' }}>
                        {formatDate(ticket.ticket.followUpAt)}
                      </span>
                    )}
                  </HeaderItem>
                )}
              <HeaderItem title="Closed">
                {formatDate(ticket.ticket.closedAt)}
              </HeaderItem>
            </div>
            <div>
              {permissions.internal.hasPermissions(userProfile) && (
                <Button
                  title={
                    ticket.organization.id === userProfile.organization.id
                      ? 'Edit Header'
                      : 'Edit Header (Switch to Ticket Organization to Edit)'
                  }
                  disabled={
                    ticket.organization.id !== userProfile.organization.id
                  }
                  type={'default'}
                  icon={<IBDIcon name={'Edit'} />}
                  onClick={() => {
                    onAction('edit-header', '');
                  }}
                />
              )}
            </div>
          </Flex>
        </Col>
      </Row>
      <HeaderItem title="Description">
        <pre style={styles.description}>{ticket.ticket.description}</pre>
      </HeaderItem>
      <br />
      <CustomerExplanationPanel />
      {isNotNull(ticket.ticket.extras) &&
        isNotNull(ticket.ticket.extras.optimize) && <OptimizePanel />}
      {permissions.internal.hasPermissions(userProfile) && (
        <TechnicianActionsPanel />
      )}
      {action === 'edit-header' && (
        <HeaderEditDialog
          ticket={ticket}
          open={action === 'edit-header'}
          onClose={(doRefresh: boolean) => {
            if (doRefresh) {
              onAction('refresh', '');
            } else {
              onAction('', '');
            }
          }}
        />
      )}
    </div>
  );
};

export default TicketHeader;
